import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Route guard for /billing/group/:groupId pages.
 *
 * Fail-closed: waits for the authoritative user-info response before deciding,
 * then requires that the requested group id is in the user's fresh
 * manageable_billing_groups list (platform admins are always allowed).
 * Without a :groupId param, redirects to the first manageable group.
 */
const GroupBillingRoute: React.FC<{ element: React.ReactNode }> = ({
  element,
}) => {
  const { accessToken, user, loading, userInfoSettled } = useAuth();
  const location = useLocation();
  const { groupId } = useParams();

  if (!accessToken) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{
          from: location.pathname,
          message: 'Please sign in to access this page.',
        }}
      />
    );
  }

  // Wait for a settled, authoritative user-info before granting or denying.
  if (loading || (!userInfoSettled && !user?.is_platform_admin)) {
    if (loading || !user) {
      return (
        <div className="flex min-h-screen items-center justify-center text-gray-900 dark:text-white">
          Loading...
        </div>
      );
    }
    // User loaded but from cache/legacy data: group eligibility is unknown.
    return <Navigate to="/billing" replace />;
  }

  const manageable = user?.manageable_billing_groups ?? [];
  const isPlatformAdmin = user?.is_platform_admin === true;

  if (!groupId) {
    // /billing/group index: send the user to their first manageable group.
    if (manageable.length > 0) {
      return <Navigate to={`/billing/group/${manageable[0].id}`} replace />;
    }
    return <Navigate to="/billing" replace />;
  }

  const allowed =
    isPlatformAdmin ||
    manageable.some((group) => String(group.id) === String(groupId));

  if (!allowed) {
    return <Navigate to="/billing" replace />;
  }

  return <>{element}</>;
};

export default GroupBillingRoute;
